import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/app/Logo";
import { Sparkles, X } from "lucide-react";

const SESSION_KEY = "ydd_unleash_modal_shown";

/**
 * "Unleash the power of your dog" marketing modal.
 * - Renders once per browser session for the logged-in user.
 * - Closing on backdrop, X button, "Let's go", or "Maybe later" all dismiss it for the rest of the session.
 */
export default function UnleashModal() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(SESSION_KEY)) return;
    // Tiny delay so it doesn't pop the second the dashboard mounts
    const t = window.setTimeout(() => setOpen(true), 700);
    return () => window.clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch (err) { console.warn("UnleashModal sessionStorage write failed:", err); }
    setOpen(false);
  };

  const goPro = () => {
    dismiss();
    nav("/subscription");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
      data-testid="unleash-modal-backdrop"
      onClick={dismiss}
    >
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-card border-2 border-primary rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="unleash-modal"
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="unleash-modal-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-5">
            <Logo size={48} textClassName="text-base" />
          </div>

          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight" data-testid="unleash-modal-headline">
            Unleash the power of your&nbsp;dog.
          </h2>

          <p className="text-sm sm:text-base text-foreground/80 mt-4 leading-relaxed">
            Whether you&apos;re raising a puppy, chasing titles, conditioning a sport dog,
            or pampering a senior — the best results come from <strong>showing up every day</strong>.
            Your Dog Days helps you make that easy.
          </p>

          <ul className="mt-5 space-y-1.5 text-sm">
            <li className="flex items-start gap-2"><Sparkles size={14} className="text-primary mt-0.5 shrink-0" /> Daily, weekly, and monthly prompts tailored to your dog</li>
            <li className="flex items-start gap-2"><Sparkles size={14} className="text-primary mt-0.5 shrink-0" /> Email reminders so nothing slips</li>
            <li className="flex items-start gap-2"><Sparkles size={14} className="text-primary mt-0.5 shrink-0" /> A journal that turns effort into measurable progress</li>
          </ul>

          <div className="flex flex-wrap items-center gap-2 mt-7">
            <Button
              onClick={goPro}
              data-testid="unleash-modal-cta"
              className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all"
            >
              <Sparkles size={14} className="mr-1.5" /> Let&apos;s go
            </Button>
            <Button
              variant="ghost"
              onClick={dismiss}
              data-testid="unleash-modal-later"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
